"use client"

import { useRouter } from "next/navigation"
import { memo } from "react"

import { Popover, PopoverTrigger, PopoverContent } from "@heroui/react"

import { Button } from "@/components/ui/button"
import { StoneFrame } from "@/components/ui/stone-frame"
import { WalletIcon } from "@/src/shared/assets/icons"
import { useAuthStore } from "@/stores/userStore"
import { userUIStore } from "@/stores/userUIStore"
import { moneyFormat } from "@/utils/money"

import { useBalanceOptimized } from "./hooks/useBalanceOptimized"
import { core } from "@/features/header/finance/localization/texts"
import ValuesCard from "./ValuesCard"

// The em box hangs below the baseline, so a centered line sits high. Trim it to the cap height.
const tightGlyphs =
  "inline-block leading-none [text-box:trim-both_cap_alphabetic]"

const walletHover =
  "transition duration-200 hover:shadow-[0_0_7px_0_--theme(--color-purple-200/45%),0_0_14px_1px_--theme(--color-purple-200/22%)]"

const walletIconClass =
  "size-5 shrink-0 text-white [&_path]:fill-current [&_path]:[fill-opacity:1]"

export function Balance() {
  const { balance } = useBalanceOptimized()
  const { account } = useAuthStore()
  const { toggleDepositModal } = userUIStore()
  const router = useRouter()

  function handleDeposit(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation()
    if (account?.contingencies?.deposits) {
      router.push("/?cont=4")
    } else {
      toggleDepositModal()
    }
  }

  const formatted = moneyFormat(balance[0]?.amount.toFixed(2) ?? "0")
  const [whole, cents = "00"] = formatted.split(".")

  return (
    <div>
      <Popover showArrow placement="bottom" className="z-0">
        <PopoverTrigger>
          <StoneFrame
            tone="dark"
            className="cursor-pointer h-11 min-w-[148px] rounded-(--stone-radius) bg-black-1000 xlg:h-[52px] xlg:w-[270px]"
            contentClassName="flex h-full items-center justify-between p-1"
          >
            <div className="flex flex-1 items-center justify-end text-sm font-medium xlg:justify-end xlg:text-[18px]">
              <span className={tightGlyphs}>
                L{" "}
                <span className="ml-[5px] mr-2.5">
                  {whole}
                  <span className="text-xs text-white/30">.{cents}</span>
                </span>
              </span>
            </div>
            <Button
              variant="hand"
              size="icon"
              className={`xlg:hidden ${walletHover}`}
              onClick={handleDeposit}
            >
              <WalletIcon viewBox="0 0 24 24" className={walletIconClass} />
            </Button>
            <Button
              variant="hand"
              className={`hidden h-full px-4 [--sketch-cap:9px] xlg:inline-flex xlg:w-[137px] ${walletHover}`}
              onClick={handleDeposit}
            >
              <span className="inline-flex items-center gap-2">
                <WalletIcon viewBox="0 0 24 24" className={walletIconClass} />
                <span className={`${tightGlyphs} text-[18px]`}>
                  {core.wallet}
                </span>
              </span>
            </Button>
          </StoneFrame>
        </PopoverTrigger>
        <PopoverContent className="p-1">
          <ValuesCard />
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default memo(Balance)
